/**
 * Gamesight Arcade SDK.
 *
 * Usage from any game (no bundler required):
 *   <script src="/sdk/v1/arcade.js"></script>
 *   <script>
 *     Arcade.init();
 *     Arcade.leaderboard.submit(1234).catch(console.warn);
 *     Arcade.leaderboard.top(10).then(renderScores);
 *   </script>
 *
 * Leaderboard calls reject with ArcadeOfflineError when the API is unreachable
 * (offline / local dev without the API running), which games should treat as
 * "leaderboard unavailable" and carry on. Accounts use the shared arcade login
 * (Google Workspace); `auth.getUser()` returns null when signed out.
 */
import type { Achievement, ArcadeConfig, ArcadeUser, NavAction, NavBack, NavDrawer, NavStat, ArcadeVoice, BoardOptions, GameAchievement, GameAchievements, Invite, MyAchievements, MyScore, RealtimeRoom, RoomMember, ScoreEntry, SubmitOptions, Unlocker, UnlockResult, VoiceSession, VoiceState } from "./types";

const PLAYER_KEY = "arcade.player";

let config: Required<ArcadeConfig> = { game: "", apiBase: "/api", realtimeUrl: "", achievementToasts: true };

export class ArcadeOfflineError extends Error {
  constructor() {
    super("Arcade API unavailable (offline, local dev, or not yet deployed)");
    this.name = "ArcadeOfflineError";
  }
}

function detectGame(): string {
  const match = /^\/games\/([a-z0-9-]+)\//.exec(location.pathname);
  return match?.[1] ?? "";
}

function requireGame(): string {
  if (!config.game) {
    throw new Error(
      "Arcade: game slug unknown. Call Arcade.init({ game: \"<slug>\" }) when not served from /games/<slug>/."
    );
  }
  return config.game;
}

async function api<T>(path: string, init?: RequestInit): Promise<T> {
  let res: Response;
  try {
    res = await fetch(`${config.apiBase}${path}`, {
      headers: { "content-type": "application/json" },
      credentials: "same-origin",
      ...init,
    });
  } catch {
    throw new ArcadeOfflineError();
  }
  // No API behind /api yet (S3 returns HTML) or genuinely missing → treat as offline.
  if (res.status === 404 || res.status === 403) throw new ArcadeOfflineError();
  if (!res.ok) throw new Error(`Arcade API error ${res.status}: ${await res.text()}`);
  return res.json() as Promise<T>;
}

/**
 * Like api(), but a signed-out caller gets null instead of an exception. Used
 * where "nobody is signed in" is an ordinary answer rather than a failure —
 * there's no one to award an achievement to, and no score to report.
 */
async function apiOrNull<T>(path: string, init?: RequestInit): Promise<T | null> {
  let res: Response;
  try {
    res = await fetch(`${config.apiBase}${path}`, {
      headers: { "content-type": "application/json" },
      credentials: "same-origin",
      ...init,
    });
  } catch {
    throw new ArcadeOfflineError();
  }
  if (res.status === 401) return null;
  if (res.status === 404 || res.status === 403) throw new ArcadeOfflineError();
  if (!res.ok) throw new Error(`Arcade API error ${res.status}: ${await res.text()}`);
  return res.json() as Promise<T>;
}

function rememberedPlayer(): string {
  let player = localStorage.getItem(PLAYER_KEY);
  if (!player) {
    player = (prompt("Enter a player name for the leaderboard:") ?? "anon").trim().slice(0, 24) || "anon";
    localStorage.setItem(PLAYER_KEY, player);
  }
  return player;
}

function init(options: ArcadeConfig = {}): void {
  config = {
    game: options.game ?? detectGame(),
    apiBase: (options.apiBase ?? "/api").replace(/\/$/, ""),
    realtimeUrl: (options.realtimeUrl ?? "").replace(/\/$/, ""),
    achievementToasts: options.achievementToasts ?? true,
  };
}

/** A game may declare several named boards; omitting one reads the default. */
const boardQuery = (opts: { board?: string }) => (opts.board ? `&board=${encodeURIComponent(opts.board)}` : "");

const leaderboard = {
  async submit(score: number, opts: SubmitOptions = {}): Promise<ScoreEntry> {
    if (!Number.isFinite(score) || score < 0) throw new Error("Arcade: score must be a non-negative finite number");
    // Prefer an explicit name, then the signed-in account, then the locally
    // remembered name. The server also attributes to the session when present.
    const signedIn = await auth.getUser().catch(() => null);
    const player = (opts.player ?? signedIn?.name ?? rememberedPlayer()).slice(0, 24);
    const body = {
      player,
      score: Math.floor(score),
      ...(opts.meta ? { meta: opts.meta } : {}),
      ...(opts.board ? { board: opts.board } : {}),
    };
    return api<ScoreEntry>(`/games/${requireGame()}/scores`, { method: "POST", body: JSON.stringify(body) });
  },

  async top(limit = 10, opts: BoardOptions = {}): Promise<ScoreEntry[]> {
    const capped = Math.min(Math.max(1, Math.floor(limit)), 100);
    const data = await api<{ scores: ScoreEntry[] }>(
      `/games/${requireGame()}/scores?limit=${capped}${boardQuery(opts)}`);
    return data.scores;
  },

  /**
   * The signed-in player's own best score for this game (and 1-based rank),
   * or null if they have no score / are signed out. Requires the shared login.
   */
  async me(opts: BoardOptions = {}): Promise<MyScore | null> {
    const data = await api<{ me: MyScore | null }>(
      `/games/${requireGame()}/scores?me=1&limit=1${boardQuery(opts)}`);
    return data.me ?? null;
  },

  /** The player name stored on this device, if any. */
  player(): string | null {
    return localStorage.getItem(PLAYER_KEY);
  },
};

// ---------------------------------------------------------------------------
// Auth — the shared arcade account (Google Workspace login). Because the whole
// arcade is same-origin, the session cookie set at sign-in is visible to every
// game automatically; no per-game login.
// ---------------------------------------------------------------------------
const listeners = new Set<(user: ArcadeUser | null) => void>();
let watching = false;

function sameUser(a: ArcadeUser | null, b: ArcadeUser | null): boolean {
  return a?.email === b?.email;
}

/** Re-check the session and notify listeners if it changed (used on tab focus). */
async function refreshUser(): Promise<void> {
  const prev = lastUser;
  const next = await auth.getUser();
  if (!sameUser(prev, next)) listeners.forEach((cb) => cb(next));
}

let lastUser: ArcadeUser | null = null;

const auth = {
  /** Resolves the signed-in user, or null when signed out / offline. */
  async getUser(): Promise<ArcadeUser | null> {
    try {
      const res = await fetch(`${config.apiBase}/auth/me`, { credentials: "same-origin" });
      lastUser = res.ok ? ((await res.json()).user ?? null) : null;
    } catch {
      lastUser = null; // offline / API not running
    }
    return lastUser;
  },

  /** Redirect to Google sign-in, returning to `opts.return` (default: current page). */
  login(opts: { return?: string } = {}): void {
    const ret = opts.return ?? location.pathname + location.search;
    location.assign(`${config.apiBase}/auth/login?return=${encodeURIComponent(ret)}`);
  },

  /** Sign out (clears the shared session) and notify listeners. */
  async logout(): Promise<void> {
    try {
      await fetch(`${config.apiBase}/auth/logout`, { method: "POST", credentials: "same-origin" });
    } finally {
      lastUser = null;
      listeners.forEach((cb) => cb(null));
    }
  },

  /** Subscribe to sign-in/out changes. Fires once with the current user. Returns an unsubscribe fn. */
  onChange(callback: (user: ArcadeUser | null) => void): () => void {
    listeners.add(callback);
    auth.getUser().then(callback);
    if (!watching && typeof window !== "undefined") {
      watching = true;
      window.addEventListener("focus", () => void refreshUser());
      document.addEventListener("visibilitychange", () => { if (!document.hidden) void refreshUser(); });
    }
    return () => listeners.delete(callback);
  },
};

// ---------------------------------------------------------------------------
// Realtime — generic rooms over WebSocket (shared platform transport). Any game
// can join a named room scoped to its slug, broadcast messages, and see who's
// present. A short-lived token from /realtime/info authenticates the socket (the
// prod socket is cross-origin, so the session cookie can't reach it); signed-out
// users can't connect. Server-authoritative game logic lives in the per-game backend.
// ---------------------------------------------------------------------------
type RoomEvent = "message" | "presence" | "open" | "close";

/**
 * Resolve where to open the socket, plus a short-lived auth token. Fetched fresh
 * on every (re)connect: the prod WebSocket is a different origin so the session
 * cookie can't reach it, and the token is short-lived — a cached one would go
 * stale and break reconnects. Locally the dev server ignores the token.
 */
async function resolveRealtimeInfo(): Promise<{ url: string; token: string | null }> {
  if (config.realtimeUrl) return { url: config.realtimeUrl, token: null };
  const r = await api<{ url: string; token?: string | null }>("/realtime/info");
  return { url: r.url, token: r.token ?? null };
}

// Reconnect backoff: capped exponential, reset on a successful open. Bounded so a
// permanently-failing connect (e.g. signed out → $connect 401) can't loop forever.
const RECONNECT_BASE_MS = 2000;
const RECONNECT_CAP_MS = 30000;
const RECONNECT_MAX_ATTEMPTS = 8;

class Room implements RealtimeRoom {
  readonly id: string;
  connected = false;
  members: RoomMember[] = [];
  private readonly game: string;
  private ws: WebSocket | null = null;
  private closed = false;
  private hasOpened = false;   // gates auto-reconnect: only retry a connection that once worked
  private attempts = 0;        // consecutive failed reconnects (reset on open)
  private ping: ReturnType<typeof setInterval> | null = null;
  private reconnect: ReturnType<typeof setTimeout> | null = null;
  private readonly listeners: Record<RoomEvent, Set<(p: any) => void>> = {
    message: new Set(), presence: new Set(), open: new Set(), close: new Set(),
  };

  constructor(id: string, game: string) { this.id = id; this.game = game; }

  /** Resolves once the socket is open the first time; reconnects silently after. */
  connect(): Promise<void> {
    return new Promise((resolve, reject) => this.openSocket(resolve, reject));
  }

  private async openSocket(onOpen?: () => void, onFail?: (e: unknown) => void): Promise<void> {
    let info: { url: string; token: string | null };
    // Failing to resolve the URL/token (offline, transient API error) is the same
    // as a closed socket: surface it to the initial caller, and let a previously
    // connected room keep retrying with backoff.
    try { info = await resolveRealtimeInfo(); } catch (e) { onFail?.(e); this.scheduleReconnect(); return; }
    const q = `game=${encodeURIComponent(this.game)}&room=${encodeURIComponent(this.id)}`;
    const url = info.token ? `${info.url}?${q}&token=${encodeURIComponent(info.token)}` : `${info.url}?${q}`;
    const ws = new WebSocket(url);
    this.ws = ws;
    ws.onopen = () => {
      this.connected = true;
      this.hasOpened = true;
      this.attempts = 0; // a good connection resets the backoff
      ws.send(JSON.stringify({ action: "join", game: this.game, room: this.id }));
      this.ping = setInterval(() => { if (ws.readyState === WebSocket.OPEN) ws.send(JSON.stringify({ action: "ping" })); }, 30000);
      this.emit("open", undefined);
      onOpen?.();
    };
    ws.onmessage = (e) => {
      let m: any;
      try { m = JSON.parse(typeof e.data === "string" ? e.data : ""); } catch { return; }
      if (m.type === "presence") { this.members = m.members ?? []; this.emit("presence", this.members); }
      else if (m.type === "message") this.emit("message", { from: m.from, data: m.data });
    };
    ws.onerror = () => onFail?.(new ArcadeOfflineError());
    ws.onclose = () => {
      this.connected = false;
      if (this.ping) { clearInterval(this.ping); this.ping = null; }
      this.emit("close", undefined);
      this.scheduleReconnect();
    };
  }

  /**
   * Reconnect with capped exponential backoff. Only retries a room that has
   * connected at least once — an initial-connect failure is surfaced to the
   * caller (connect() rejects) instead of looping on an orphan they can't close.
   * Gives up after RECONNECT_MAX_ATTEMPTS so a permanent failure (signed out,
   * revoked) can't spin forever; close() or a fresh join() can recover.
   */
  private scheduleReconnect(): void {
    if (this.closed || this.reconnect || !this.hasOpened) return;
    if (this.attempts >= RECONNECT_MAX_ATTEMPTS) return;
    const delay = Math.min(RECONNECT_CAP_MS, RECONNECT_BASE_MS * 2 ** this.attempts);
    this.attempts++;
    this.reconnect = setTimeout(() => { this.reconnect = null; void this.openSocket(); }, delay);
  }

  send(data: unknown): void {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify({ action: "send", room: this.id, data }));
    }
  }

  match(data: unknown): void {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify({ action: "match", room: this.id, data }));
    }
  }

  on(event: RoomEvent, cb: (p: any) => void): () => void {
    this.listeners[event].add(cb);
    return () => this.listeners[event].delete(cb);
  }

  private emit(event: RoomEvent, payload: any): void {
    this.listeners[event].forEach((cb) => cb(payload));
  }

  close(): void {
    this.closed = true;
    if (this.reconnect) { clearTimeout(this.reconnect); this.reconnect = null; }
    if (this.ping) { clearInterval(this.ping); this.ping = null; }
    this.ws?.close();
    this.ws = null;
    this.connected = false;
  }
}

const realtime = {
  /** Join (or create) a realtime room scoped to your game. Resolves once connected. */
  async join(roomId: string): Promise<RealtimeRoom> {
    const room = new Room(roomId, requireGame());
    await room.connect();
    return room;
  },
};

// ---------------------------------------------------------------------------
// Invites — player-to-player invitations that land in the recipient's inbox
// (drives the notification badge). Created/listed over the HTTP API; the live
// session itself runs over a realtime room referenced by the invite payload.
// ---------------------------------------------------------------------------
const inviteListeners = new Set<(items: Invite[]) => void>();
let invitePoll: ReturnType<typeof setInterval> | null = null;
async function refreshInvites(): Promise<void> {
  try {
    const items = await invites.inbox();
    inviteListeners.forEach((cb) => cb(items));
  } catch { /* signed out / offline — leave listeners as-is */ }
}

const invites = {
  /** Send an invite to another player (by account id / email or display name). */
  async send(opts: { to: string; kind: string; payload?: Record<string, unknown> }): Promise<Invite> {
    const body = { to: opts.to, kind: opts.kind, game: requireGame(), payload: opts.payload ?? {} };
    return api<Invite>("/invites", { method: "POST", body: JSON.stringify(body) });
  },
  /** The signed-in player's pending invites. */
  async inbox(): Promise<Invite[]> {
    return (await api<{ invites: Invite[] }>("/invites")).invites;
  },
  async accept(id: string): Promise<Invite> {
    return api<Invite>(`/invites/${encodeURIComponent(id)}/accept`, { method: "POST" });
  },
  async decline(id: string): Promise<Invite> {
    return api<Invite>(`/invites/${encodeURIComponent(id)}/decline`, { method: "POST" });
  },
  /** Subscribe to inbox changes (polls + refreshes on focus). Fires once immediately. Returns unsubscribe. */
  onChange(cb: (items: Invite[]) => void): () => void {
    inviteListeners.add(cb);
    void refreshInvites();
    if (!invitePoll && typeof window !== "undefined") {
      invitePoll = setInterval(() => void refreshInvites(), 20000);
      window.addEventListener("focus", () => void refreshInvites());
    }
    return () => inviteListeners.delete(cb);
  },
};

/** Player directory — typeahead search for invites (signed-in arcade accounts). */
const players = {
  async search(query: string): Promise<RoomMember[]> {
    const q = String(query || "").trim();
    if (!q) return [];
    return (await api<{ players: RoomMember[] }>(`/accounts/search?q=${encodeURIComponent(q)}`)).players;
  },
};

// ---------------------------------------------------------------------------
// Voice — peer-to-peer WebRTC voice over a realtime room (1v1). The room is the
// signaling channel: SDP/ICE ride over room.send()/on("message") under a
// "voice-*" namespace, the peer keyed by from.accountId. Audio is peer-to-peer;
// our servers never see it. STUN-only today (getIceServers) — strict NATs / VPN
// may fail to connect, surfaced as status "failed".
// ---------------------------------------------------------------------------

function getIceServers(): RTCIceServer[] {
  // STUN-only MVP. To add a TURN relay later, replace this body with a fetch of
  // short-lived creds (mirrors /realtime/info):
  //   return (await api<{ iceServers: RTCIceServer[] }>("/realtime/voice-ice")).iceServers;
  return [{ urls: "stun:stun.l.google.com:19302" }];
}

/** Deterministic, glare-free role: the smaller accountId offers, the other answers. */
function isOfferer(myId: string, peerId: string): boolean {
  return myId < peerId;
}

const VOICE_STYLE_ID = "arcade-voice-styles";
function injectVoiceStyles(): void {
  if (typeof document === "undefined" || document.getElementById(VOICE_STYLE_ID)) return;
  const css = `
.arc-voice{display:inline-flex;align-items:center;gap:.5rem;font-family:"Space Grotesk",system-ui,sans-serif}
.arc-voice-btn{display:inline-flex;align-items:center;gap:.4rem;font:inherit;font-size:.85rem;font-weight:600;background:#15152e;border:1px solid #2e2e5e;border-radius:999px;padding:.4rem .8rem;cursor:pointer;color:#f5f7ff}
.arc-voice-btn:hover{border-color:#5555f2}
.arc-voice-btn[disabled]{opacity:.6;cursor:default}
.arc-voice-btn.live{background:#1d1d40;border-color:#5555f2}
.arc-voice-btn.muted{background:#3a1d2a;border-color:#f55b53}
.arc-voice-status{font-size:.78rem;color:#9a9ad6}
.arc-voice-peer{display:inline-flex;align-items:center;gap:.3rem;font-size:.8rem;color:#9a9ad6}
.arc-voice-dot{width:.6rem;height:.6rem;border-radius:999px;background:#3a3a64;box-shadow:0 0 0 0 #5ad17a00;transition:box-shadow .12s,background .12s}
.arc-voice-dot.speaking{background:#5ad17a;box-shadow:0 0 0 3px #5ad17a55}
.arc-voice-dot.muted{background:#f55b53}`;
  const el = document.createElement("style");
  el.id = VOICE_STYLE_ID;
  el.textContent = css;
  document.head.appendChild(el);
}

class VoiceSessionImpl implements VoiceSession {
  private readonly room: RealtimeRoom;
  private myId: string | null = null;
  private peerId: string | null = null;
  private pc: RTCPeerConnection | null = null;
  private localStream: MediaStream | null = null;
  private remoteAudio: HTMLAudioElement | null = null;
  private pendingOffer: RTCSessionDescriptionInit | null = null;
  private readonly pendingIce: RTCIceCandidateInit[] = [];
  private joined = false;
  private peerReady = false;
  private state: VoiceState = { status: "idle", muted: false, peerPresent: false, peerMuted: false, speaking: false, peerSpeaking: false };
  private readonly stateListeners = new Set<(s: VoiceState) => void>();
  // speaking detection (Web Audio) — local only, no extra signaling
  private audioCtx: AudioContext | null = null;
  private vadRaf: number | null = null;
  private localAnalyser: AnalyserNode | null = null;
  private remoteAnalyser: AnalyserNode | null = null;

  constructor(room: RealtimeRoom) {
    this.room = room;
    room.on("message", (m: { from: RoomMember; data: any }) => void this.onMessage(m.from, m.data));
    room.on("presence", (members: RoomMember[]) => this.onPresence(members));
    room.on("open", () => { if (this.joined) this.signal("voice-ready", { muted: this.state.muted }); });
    // accountId === email; needed for the offerer decision and to spot the peer in presence.
    void auth.getUser().then((u) => { this.myId = u?.email ?? null; this.onPresence(room.members); });
  }

  on(event: "state", cb: (s: VoiceState) => void): () => void {
    this.stateListeners.add(cb);
    cb(this.state);
    return () => this.stateListeners.delete(cb);
  }

  private setState(patch: Partial<VoiceState>): void {
    this.state = { ...this.state, ...patch };
    this.stateListeners.forEach((cb) => cb(this.state));
  }

  private signal(t: string, data: Record<string, unknown> = {}): void {
    this.room.send({ t, ...data });
  }

  private onPresence(members: RoomMember[]): void {
    const peer = members.find((m) => m.accountId !== this.myId) ?? null;
    if (peer) this.peerId = peer.accountId;
    const present = !!peer;
    if (!present) { this.peerReady = false; this.teardownRemote(); }
    this.setState({ peerPresent: present, peerMuted: present && this.state.peerMuted, peerSpeaking: present && this.state.peerSpeaking });
    if (present) this.maybeNegotiate();
  }

  async join(): Promise<void> {
    if (this.joined) return;
    if (typeof navigator === "undefined" || !navigator.mediaDevices?.getUserMedia) { this.setState({ status: "failed" }); return; }
    if (!this.myId) this.myId = (await auth.getUser())?.email ?? null;
    this.setState({ status: "connecting" });
    try {
      this.localStream = await navigator.mediaDevices.getUserMedia({ audio: true, video: false });
    } catch { this.setState({ status: "failed" }); return; }
    this.startLocalVad(this.localStream);
    const pc = new RTCPeerConnection({ iceServers: getIceServers() });
    this.pc = pc;
    for (const track of this.localStream.getAudioTracks()) { track.enabled = !this.state.muted; pc.addTrack(track, this.localStream); }
    pc.onicecandidate = (e) => { if (e.candidate) this.signal("voice-ice", { candidate: e.candidate.toJSON() }); };
    pc.ontrack = (e) => this.attachRemote(e.streams[0]);
    pc.onconnectionstatechange = () => {
      const s = pc.connectionState;
      if (s === "connected") this.setState({ status: "connected" });
      else if (s === "failed" || s === "closed") this.setState({ status: this.joined ? "failed" : "idle" });
      else if (s === "disconnected") this.setState({ status: "connecting" });
    };
    this.joined = true;
    this.signal("voice-ready", { muted: this.state.muted });
    if (this.pendingOffer) { const o = this.pendingOffer; this.pendingOffer = null; await this.handleOffer(o); }
    else this.maybeNegotiate();
  }

  private maybeNegotiate(): void {
    if (!this.joined || !this.peerReady || !this.pc || !this.myId || !this.peerId) return;
    if (!isOfferer(this.myId, this.peerId)) return;          // answerer waits for the offer
    if (this.pc.signalingState !== "stable") return;
    void this.makeOffer();
  }

  private async makeOffer(): Promise<void> {
    if (!this.pc) return;
    const offer = await this.pc.createOffer();
    await this.pc.setLocalDescription(offer);
    this.signal("voice-offer", { sdp: this.pc.localDescription });
  }

  private async handleOffer(sdp: RTCSessionDescriptionInit): Promise<void> {
    if (!this.joined) { this.pendingOffer = sdp; return; } // offer arrived before we grabbed the mic
    if (!this.pc) return;
    await this.pc.setRemoteDescription(sdp);
    await this.drainIce();
    const answer = await this.pc.createAnswer();
    await this.pc.setLocalDescription(answer);
    this.signal("voice-answer", { sdp: this.pc.localDescription });
  }

  private async drainIce(): Promise<void> {
    while (this.pc && this.pendingIce.length) {
      try { await this.pc.addIceCandidate(this.pendingIce.shift()!); } catch { /* stale candidate */ }
    }
  }

  private async onMessage(from: RoomMember, data: any): Promise<void> {
    const t = data?.t;
    if (typeof t !== "string" || !t.startsWith("voice-")) return; // not ours — ignore game traffic
    if (from.accountId === this.myId) return;
    this.peerId = from.accountId;
    try {
      if (t === "voice-ready") { this.peerReady = true; this.setState({ peerPresent: true, peerMuted: !!data.muted }); this.maybeNegotiate(); }
      else if (t === "voice-offer" && data.sdp) await this.handleOffer(data.sdp);
      else if (t === "voice-answer" && data.sdp && this.pc) { await this.pc.setRemoteDescription(data.sdp); await this.drainIce(); }
      else if (t === "voice-ice" && data.candidate) {
        if (this.pc?.remoteDescription) { try { await this.pc.addIceCandidate(data.candidate); } catch { /* ignore */ } }
        else this.pendingIce.push(data.candidate);
      }
      else if (t === "voice-state") this.setState({ peerMuted: !!data.muted });
      else if (t === "voice-bye") { this.peerReady = false; this.teardownRemote(); this.setState({ peerPresent: false, peerMuted: false, peerSpeaking: false }); }
    } catch { /* malformed peer signaling — connection will retry or fail */ }
  }

  setMuted(muted: boolean): void {
    if (this.localStream) for (const tr of this.localStream.getAudioTracks()) tr.enabled = !muted;
    this.setState({ muted });
    this.signal("voice-state", { muted });
  }

  private attachRemote(stream: MediaStream): void {
    if (!this.remoteAudio) {
      const el = document.createElement("audio");
      el.autoplay = true;
      el.style.display = "none";
      document.body.appendChild(el);
      this.remoteAudio = el;
    }
    this.remoteAudio.srcObject = stream;
    void this.remoteAudio.play?.().catch(() => { /* autoplay allowed after the join gesture */ });
    this.startRemoteVad(stream);
  }

  private teardownRemote(): void {
    this.remoteAnalyser = null;
    if (this.remoteAudio) { this.remoteAudio.srcObject = null; this.remoteAudio.remove(); this.remoteAudio = null; }
  }

  // ── speaking detection (local Web Audio RMS on each stream) ─────────────────
  private ctx(): AudioContext {
    if (!this.audioCtx) this.audioCtx = new (window.AudioContext || (window as any).webkitAudioContext)();
    if (this.audioCtx.state === "suspended") void this.audioCtx.resume();
    return this.audioCtx;
  }

  private makeAnalyser(stream: MediaStream): AnalyserNode {
    const ctx = this.ctx();
    const src = ctx.createMediaStreamSource(stream);
    const an = ctx.createAnalyser();
    an.fftSize = 512;
    src.connect(an);
    return an;
  }

  private startLocalVad(stream: MediaStream): void { this.localAnalyser = this.makeAnalyser(stream); this.startVad(); }
  private startRemoteVad(stream: MediaStream): void { this.remoteAnalyser = this.makeAnalyser(stream); this.startVad(); }

  private static rms(an: AnalyserNode, buf: Uint8Array<ArrayBuffer>): number {
    an.getByteTimeDomainData(buf);
    let sum = 0;
    for (let i = 0; i < buf.length; i++) { const v = (buf[i] - 128) / 128; sum += v * v; }
    return Math.sqrt(sum / buf.length);
  }

  private startVad(): void {
    if (this.vadRaf != null || typeof requestAnimationFrame === "undefined") return;
    const buf = new Uint8Array(new ArrayBuffer(256));
    const THRESHOLD = 0.04;
    const tick = () => {
      const speaking = !!this.localAnalyser && !this.state.muted && VoiceSessionImpl.rms(this.localAnalyser, buf) > THRESHOLD;
      const peerSpeaking = !!this.remoteAnalyser && VoiceSessionImpl.rms(this.remoteAnalyser, buf) > THRESHOLD;
      if (speaking !== this.state.speaking || peerSpeaking !== this.state.peerSpeaking) this.setState({ speaking, peerSpeaking });
      this.vadRaf = requestAnimationFrame(tick);
    };
    this.vadRaf = requestAnimationFrame(tick);
  }

  private stopAnalysers(): void {
    if (this.vadRaf != null) { cancelAnimationFrame(this.vadRaf); this.vadRaf = null; }
    this.localAnalyser = null;
    this.remoteAnalyser = null;
    if (this.audioCtx) { void this.audioCtx.close().catch(() => {}); this.audioCtx = null; }
  }

  leave(): void {
    if (this.joined) this.signal("voice-bye", {});
    this.joined = false;
    this.stopAnalysers();
    if (this.localStream) { for (const tr of this.localStream.getTracks()) tr.stop(); this.localStream = null; }
    if (this.pc) { this.pc.onicecandidate = null; this.pc.ontrack = null; this.pc.onconnectionstatechange = null; this.pc.close(); this.pc = null; }
    this.teardownRemote();
    this.pendingOffer = null;
    this.pendingIce.length = 0;
    this.peerReady = false;
    this.setState({ status: "idle", speaking: false, peerSpeaking: false });
  }

  mountControls(el: HTMLElement): () => void {
    injectVoiceStyles();
    el.classList.add("arc-voice");
    el.innerHTML =
      '<button class="arc-voice-btn" type="button">🎙 Join voice</button>' +
      '<span class="arc-voice-peer" hidden><span class="arc-voice-dot"></span><span class="arc-voice-peer-label">Opponent</span></span>' +
      '<span class="arc-voice-status"></span>';
    const btn = el.querySelector(".arc-voice-btn") as HTMLButtonElement;
    const peer = el.querySelector(".arc-voice-peer") as HTMLElement;
    const dot = el.querySelector(".arc-voice-dot") as HTMLElement;
    const status = el.querySelector(".arc-voice-status") as HTMLElement;

    const onClick = () => {
      const s = this.state;
      if (s.status === "idle" || s.status === "failed") void this.join();
      else this.setMuted(!s.muted);
    };
    btn.addEventListener("click", onClick);

    const render = (s: VoiceState) => {
      btn.classList.toggle("live", s.status === "connected" && !s.muted);
      btn.classList.toggle("muted", s.muted && s.status !== "idle");
      btn.disabled = s.status === "connecting";
      btn.textContent =
        s.status === "idle" ? "🎙 Join voice" :
        s.status === "connecting" ? "Connecting…" :
        s.status === "failed" ? "🎙 Retry voice" :
        s.muted ? "🔇 Unmute" : "🎙 Mute";
      peer.hidden = !s.peerPresent;
      dot.classList.toggle("muted", s.peerMuted);
      dot.classList.toggle("speaking", s.peerSpeaking && !s.peerMuted);
      status.textContent =
        s.status === "failed" ? "Couldn't connect" :
        s.status === "connected" && !s.peerPresent ? "Waiting for opponent…" : "";
    };
    const unsub = this.on("state", render);

    return () => { unsub(); btn.removeEventListener("click", onClick); el.classList.remove("arc-voice"); el.innerHTML = ""; };
  }
}

const voice: ArcadeVoice = {
  attach(room: RealtimeRoom): VoiceSession { return new VoiceSessionImpl(room); },
};

// ---------------------------------------------------------------------------
// Notifications — a drop-in bell + badge + dropdown widget any game can mount.
// Driven by Arcade.invites (cross-game: an invite to ANY game shows up here),
// fully self-contained (injects its own styles), themed to the arcade.
// ---------------------------------------------------------------------------
const NOTIF_STYLE_ID = "arcade-notif-styles";
const esc = (s: unknown) =>
  String(s ?? "").replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c] as string));

function injectNotifStyles(): void {
  if (typeof document === "undefined" || document.getElementById(NOTIF_STYLE_ID)) return;
  const css = `
.arc-notif{position:relative;display:inline-block;font-family:"Space Grotesk",system-ui,sans-serif}
.arc-notif-btn{position:relative;font-size:1.1rem;line-height:1;background:#15152e;border:1px solid #2e2e5e;border-radius:999px;width:2.3rem;height:2.3rem;cursor:pointer;color:#f5f7ff}
.arc-notif-btn:hover{border-color:#5555f2}
.arc-notif-badge{position:absolute;top:-7px;right:-7px;box-sizing:border-box;display:inline-flex;align-items:center;justify-content:center;min-width:18px;height:18px;padding:0 5px;border-radius:999px;background:#f55b53;color:#fff;font-weight:700;font-size:11px;line-height:1;letter-spacing:0;box-shadow:0 0 0 2px #0a0a1f}
.arc-notif-badge[hidden]{display:none}
.arc-notif-menu{position:absolute;top:calc(100% + .5rem);right:0;width:19rem;background:#15152e;border:1px solid #2e2e5e;border-radius:12px;padding:.5rem;box-shadow:0 14px 36px #000a;z-index:9999;color:#f5f7ff}
.arc-notif-menu[hidden]{display:none}
.arc-notif-head{font-weight:700;font-size:.85rem;color:#9a9ad6;padding:.2rem .5rem .5rem}
.arc-notif-list{display:flex;flex-direction:column;gap:.4rem;max-height:60vh;overflow:auto}
.arc-notif-item{background:#0d1320cc;border:1px solid #2e2e5e;border-radius:9px;padding:.55rem .65rem}
.arc-notif-text{font-size:.9rem;display:flex;align-items:flex-start;gap:.55rem}
.arc-notif-body{display:flex;flex-direction:column;min-width:0;flex:1 1 auto}
.arc-notif-text strong{font-size:.92rem;line-height:1.25;overflow:hidden;text-overflow:ellipsis}
/* The game gets a line of its own above the headline: a notice can be about a
   game the player isn't looking at, so it has to say which, and tacked onto the
   end of the detail line it read as an afterthought. */
.arc-notif-kick{display:flex;align-items:baseline;justify-content:space-between;gap:.6rem}
.arc-notif-game{font-size:.68rem;font-weight:700;letter-spacing:.09em;text-transform:uppercase;color:#8a8aff;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
.arc-notif-when{flex:0 0 auto;font-size:.68rem;color:#6f6f9c;white-space:nowrap}
.arc-notif-item em{font-style:normal;color:#9a9ad6;font-size:.76rem}
/* Text links, not buttons: three notices deep, a stack of filled buttons is all
   you see. Both read the same and sit bottom-right, out of the way of the
   thing the notice is actually telling you. */
.arc-notif-actions{display:flex;justify-content:flex-end;gap:.9rem;margin:.35rem 0 0}
.arc-notif-act{font:inherit;font-size:.78rem;font-weight:600;border:0;background:none;padding:0;cursor:pointer;color:#9a9ad6}
.arc-notif-act:hover{color:#f5f7ff;text-decoration:underline}
.arc-notif-empty{color:#9a9ad6;font-size:.88rem;text-align:center;padding:.8rem 0;margin:0}
.arc-notif-ach{border-color:#f5cf2266;background:#1a1608cc}
.arc-notif-lb{border-color:#46f2fb44;background:#08191acc}
.arc-notif-lb em{color:#46f2fb}
.arc-notif-lb .arc-notif-game{color:#46f2fb}
.arc-notif-ach em{color:#f5cf22;font-weight:700}
.arc-notif-ach .arc-notif-game{color:#f5cf22;opacity:.85}
/* Points then name, description under it: the same reading order as a row on
   the game's own cabinet page.
   One line of ordinary text rather than a flex row — flex aligns boxes, and
   the points' box is sized by an inline SVG, which left the digits a couple of
   pixels off the title however the boxes were aligned. In normal inline flow
   the digits and the name share one baseline by construction, and the badge
   hangs off that baseline like a currency symbol. */
.arc-notif-title{display:block;min-width:0;font-size:.92rem;line-height:1.3;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
.arc-notif-title em{margin-right:.42em}
.arc-notif-title strong{font-size:inherit}
.arc-notif-ach .arc-notif-title .arc-gs{display:inline}
.arc-notif-ach .arc-notif-title .arc-gs-badge{display:inline-block;vertical-align:-0.135em;margin-right:.16em}
.arc-notif-desc{color:#9a9ad6;font-size:.76rem;line-height:1.3;display:-webkit-box;-webkit-line-clamp:2;-webkit-box-orient:vertical;overflow:hidden}
.arc-notif-ach .arc-gs{display:inline-flex;align-items:center;gap:.28em;white-space:nowrap;font-variant-numeric:tabular-nums}
.arc-notif-ach .arc-gs-badge{width:1em;height:1em;flex:0 0 auto;overflow:visible}
.arc-notif .arc-sr{position:absolute;width:1px;height:1px;margin:-1px;padding:0;overflow:hidden;clip-path:inset(50%);white-space:nowrap;border:0}
.arc-notif-icon{width:1.9rem;height:1.9rem;flex:0 0 1.9rem;display:grid;place-items:center;font-size:1.05rem;background:#0b0f1c;border:1px solid #2e2e5e;border-radius:7px;overflow:hidden}
.arc-notif-icon img{width:100%;height:100%;object-fit:cover}`;
  const el = document.createElement("style");
  el.id = NOTIF_STYLE_ID;
  el.textContent = css;
  document.head.appendChild(el);
}

/**
 * The arcade top bar is on every page, games included, so a game that used to
 * draw its own header has nowhere to put the live numbers it genuinely needs
 * (coins, cards, lives). `Arcade.nav` gives it an optional drawer that hangs off
 * the bar's bottom edge: the game's name on the left, its values on the right.
 *
 * Deliberately declarative rather than handing over an element: the arcade owns
 * the styling, so a dozen games can't produce a dozen different-looking bars.
 * Calls before the bar has rendered are remembered and applied when it appears.
 */
const nav = {
  /**
   * Open (or update) this game's drawer.
   *  - `name`  heading on the left. Defaults to the game slug.
   *  - `icon`  emoji or image URL beside the name. Optional.
   *  - `stats` up to 4 values on the right. Optional.
   *  - `actions` up to 3 buttons, in their own drawer under the account menu.
   */
  open(opts: NavDrawer = {}): void {
    drawer = {
      name: opts.name ?? config.game ?? "",
      icon: opts.icon ?? "",
      actions: Array.isArray(opts.actions) ? opts.actions.slice(0, 3) : [],
      stats: Array.isArray(opts.stats) ? opts.stats.slice(0, 4) : [],
      back: opts.back ?? null,
    };
    paintDrawer();
  },

  /**
   * Replace just the buttons, keeping the name, icon and values. These get their
   * own drawer under the account menu — a game's own chrome (Back, Save, Cancel,
   * Invite) belongs there rather than floating over the playfield where the
   * fixed bar can cover it. A button can also open a menu or a panel; see
   * NavAction.
   */
  actions(items: NavAction[]): void {
    if (!drawer) return this.open({ actions: items });
    drawer.actions = Array.isArray(items) ? items.slice(0, 3) : [];
    paintDrawer();
  },

  /**
   * Show or hide the back arrow beside the game's name. Pass null on the screen a player
   * would leave the game from — the arcade's own links already do that.
   */
  back(action: NavBack | null): void {
    if (!drawer) return this.open({ back: action });
    drawer.back = action ?? null;
    paintDrawer();
  },

  /** Update just the values, leaving the name and icon alone. */
  stats(items: NavStat[]): void {
    if (!drawer) return this.open({ stats: items });
    drawer.stats = Array.isArray(items) ? items.slice(0, 4) : [];
    paintDrawer();
  },

  /** Close the drawer and give the height back. */
  clear(): void {
    drawer = null;
    paintDrawer();
  },

  /**
   * Guard against leaving mid-game. The arcade bar sits over every game, so a
   * mis-aimed click on "Arcade" or "Docs" could bin a match in progress; this
   * makes that click ask first.
   *
   *   Arcade.nav.guard("You'll forfeit this match.");   // on, with a reason
   *   Arcade.nav.guard(true);                           // on, generic wording
   *   Arcade.nav.guard(false);                          // off
   *
   * Turn it on when a match starts and off when it ends. Two things are covered:
   * clicks on links that leave the game (our own dialog, so we can say what is
   * at stake), and closing or reloading the tab (the browser's dialog, whose
   * wording browsers control and ignore ours).
   */
  guard(on: boolean | string): void {
    guardMessage = on === false ? null
      : typeof on === "string" && on.trim() ? on.trim()
      : "Leaving now will lose your progress in this game.";
    installGuards();
  },
};

let drawer: Required<NavDrawer> | null = null;

// ── leaving-the-game guard ───────────────────────────────────────────────────
let guardMessage: string | null = null;
let guardsInstalled = false;

function installGuards(): void {
  if (guardsInstalled || typeof document === "undefined") return;
  guardsInstalled = true;

  // Close / reload / typed URL. The browser shows its own wording; setting
  // returnValue is the only part we control.
  window.addEventListener("beforeunload", (e) => {
    if (!guardMessage) return;
    e.preventDefault();
    (e as BeforeUnloadEvent).returnValue = guardMessage;
  });

  // In-page links, including the arcade bar's. Capture phase so we get there
  // before any handler the page has of its own.
  document.addEventListener("click", (e) => {
    if (!guardMessage) return;
    const a = (e.target as HTMLElement)?.closest?.("a[href]") as HTMLAnchorElement | null;
    if (!a || a.target === "_blank" || a.hasAttribute("download")) return;

    const href = a.getAttribute("href") || "";
    if (href.startsWith("#") || /^(mailto:|tel:|javascript:)/i.test(href)) return;

    let dest: URL;
    try { dest = new URL(a.href, location.href); } catch { return; }
    if (dest.origin !== location.origin) return;              // let the browser guard it
    // Staying inside this game (a hash, or a deeper path) isn't leaving.
    const gamePath = config.game ? `/games/${config.game}/` : null;
    if (gamePath && dest.pathname.startsWith(gamePath)) return;
    if (dest.pathname === location.pathname) return;

    if (!confirm(guardMessage)) {
      e.preventDefault();
      e.stopPropagation();
    }
  }, true);
}


function paintDrawer(): void {
  if (typeof document === "undefined") return;
  const slot = document.getElementById("arcade-nav-game");
  if (!slot) {
    // nav.js is deferred, so a game calling this at startup usually beats it.
    // Retry next frame rather than dropping the values on the floor.
    if (drawer) requestAnimationFrame(paintDrawer);
    return;
  }

  if (!drawer) {
    slot.hidden = true;
    slot.innerHTML = "";
    paintTools();                       // takes the actions drawer with it
    document.documentElement.style.removeProperty("--arcade-nav-h");
    return;
  }

  const art = drawer.icon
    ? (drawer.icon.startsWith("/") || drawer.icon.startsWith("http")
        ? `<img src="${esc(drawer.icon)}" alt="">`
        : `<span class="nav-drawer-emblem" aria-hidden="true">${esc(drawer.icon)}</span>`)
    : "";

  const back = drawer.back;
  slot.innerHTML =
    `<span class="nav-drawer-game">${art}${drawer.name ? `<span>${esc(drawer.name)}</span>` : ""}</span>` +
    (back
      ? `<button class="nav-drawer-back" type="button" data-arc-back="1" aria-label="${esc(back.label ?? "Back")}"` +
        ` title="${esc(back.label ?? "Back")}"><span aria-hidden="true">\u2190</span></button>`
      : "");
  slot.hidden = !(drawer.name || back);
  // Rebound every paint because the button is new markup each time.
  if (back) {
    slot.querySelector("[data-arc-back]")?.addEventListener("click", (e) => {
      e.preventDefault();
      try { back.onClick(); } catch { /* the game's handler is its own problem */ }
    });
  }

  paintTools();

  measureNav();
  // On a narrow screen the actions drawer moves down by the height published
  // just above, which it can only do on the next layout — so measure again once
  // that has happened. (Publishing --arcade-nav-h moves page content, never the
  // bar, so this settles rather than looping.)
  scheduleMeasure();
}

/**
 * Publish the strip the bar occupies as --arcade-nav-h: from the top of the bar
 * to whichever piece hangs lowest. That covers both drawer layouts — side by
 * side on a wide screen, stacked on a narrow one — without either case knowing
 * about the other. Games clear the bar with this, and the arcade reserves it as
 * body padding on every game page.
 *
 * Deliberately not --arcade-bar-h: that one reserves the bar's own height and
 * must not grow, or the bar itself would stretch.
 */
let navResizeBound = false;
let measurePending = false;

/**
 * Re-measure after the browser has had a layout pass. A timeout rather than
 * requestAnimationFrame on purpose: rAF doesn't tick in a headless render, and
 * reading a rect forces the layout we need anyway.
 */
function scheduleMeasure(): void {
  if (measurePending || typeof setTimeout !== "function") return;
  measurePending = true;
  setTimeout(() => { measurePending = false; measureNav(); }, 0);
}

function measureNav(): void {
  const bar = document.getElementById("arcade-nav");
  const slot = document.getElementById("arcade-nav-game");
  const tools = document.getElementById("arcade-nav-tools");
  if (!bar || !slot) return;
  const barRect = bar.getBoundingClientRect();
  const leftH = slot.hidden ? 0 : slot.getBoundingClientRect().height;
  document.documentElement.style.setProperty("--arcade-drawer-h", `${Math.round(leftH)}px`);
  const total = Math.max(
    barRect.height,
    slot.hidden ? 0 : slot.getBoundingClientRect().bottom - barRect.top,
    tools && !tools.hidden ? tools.getBoundingClientRect().bottom - barRect.top : 0
  );
  if (total > 0) document.documentElement.style.setProperty("--arcade-nav-h", `${Math.round(total)}px`);

  // Resizing past the breakpoint stacks or unstacks the drawers, and wrapping
  // the bar's own links changes its height, so re-measure rather than leave a
  // stale strip behind.
  if (!navResizeBound && typeof window !== "undefined") {
    navResizeBound = true;
    window.addEventListener("resize", scheduleMeasure);
  }
}

// ── the actions drawer ───────────────────────────────────────────────────────
// A game's own controls — Back, Save, Invite — in a drawer of their own under
// the account menu, so they read as "things I can do here" rather than as part
// of the game's identity strip on the left. A button can be plain, or open a
// menu of rows, or open a panel the game fills itself (a search field, a short
// form) so an action like inviting a player never costs a page.

/** The open dropdown, if any: which action index, and how to tear it down. */
let toolOpen: { index: number; cleanup?: (() => void) | void } | null = null;
let toolDismissBound = false;

function closeTool(): void {
  if (!toolOpen) return;
  try { if (typeof toolOpen.cleanup === "function") toolOpen.cleanup(); } catch { /* the game's teardown is its own problem */ }
  toolOpen = null;
  const host = document.getElementById("arcade-nav-tools");
  host?.querySelectorAll<HTMLElement>(".nav-tool").forEach((b) => b.setAttribute("aria-expanded", "false"));
  host?.querySelectorAll<HTMLElement>(".nav-tool-pop").forEach((p) => p.remove());
}

function iconHtml(icon: string | undefined, cls = ""): string {
  const ic = String(icon ?? "");
  if (!ic) return "";
  const c = cls ? ` class="${cls}"` : "";
  return ic.startsWith("/") || ic.startsWith("http")
    ? `<img${c} src="${esc(ic)}" alt="">`
    : `<span${c} aria-hidden="true">${esc(ic)}</span>`;
}

/**
 * The game's live values. They sit in the actions drawer, after the buttons: the left
 * drawer says what this game is, and a coin count is not that.
 */
function statsHtml(items: NavStat[]): string {
  if (!items.length) return "";
  return '<span class="nav-game">' +
    items.map((st) => {
      const icon = String(st.icon ?? "");
      const ia = icon.startsWith("/") || icon.startsWith("http")
        ? `<span class="nav-stat-icon"><img src="${esc(icon)}" alt=""></span>`
        : icon ? `<span class="nav-stat-icon" aria-hidden="true">${esc(icon)}</span>` : "";
      const title = st.label ? ` title="${esc(st.label)}"` : "";
      const sr = st.label ? `<span class="arc-sr">${esc(st.label)}: </span>` : "";
      return `<span class="nav-stat"${title}>${ia}${sr}<b>${esc(String(st.value ?? ""))}</b></span>`;
    }).join("") +
    "</span>";
}

function paintTools(): void {
  const host = document.getElementById("arcade-nav-tools");
  if (!host) {
    if (drawer?.actions.length || drawer?.stats.length) requestAnimationFrame(paintTools);
    return;
  }
  closeTool();
  const items = drawer?.actions ?? [];
  const values = drawer?.stats ?? [];
  if (!items.length && !values.length) {
    host.hidden = true;
    host.innerHTML = "";
    return;
  }

  host.innerHTML = items.map((a, i) => {
    const kind = a.menu?.length || typeof a.panel === "function" ? " nav-tool-drop" : "";
    const caret = kind ? '<span class="nav-tool-caret" aria-hidden="true">▾</span>' : "";
    return `<button class="nav-tool${a.primary ? " is-primary" : ""}${kind}" type="button" data-arc-tool="${i}"` +
      `${a.disabled ? " disabled" : ""}${kind ? ' aria-haspopup="true" aria-expanded="false"' : ""}>` +
      `${iconHtml(a.icon)}${esc(a.label ?? "")}${caret}</button>`;
  }).join("") + statsHtml(values);
  host.hidden = false;

  host.querySelectorAll<HTMLElement>("[data-arc-tool]").forEach((btn) => {
    btn.addEventListener("click", (e) => {
      e.preventDefault();
      e.stopPropagation();
      const i = Number(btn.dataset.arcTool);
      const a = drawer?.actions[i];
      if (!a) return;
      const dropping = !!(a.menu?.length || typeof a.panel === "function");
      if (!dropping) {
        closeTool();
        if (typeof a.onClick === "function") a.onClick();
        return;
      }
      const wasOpen = toolOpen?.index === i;
      closeTool();
      if (wasOpen) return;              // second click closes
      openTool(btn, a, i);
    });
  });

  if (!toolDismissBound) {
    toolDismissBound = true;
    document.addEventListener("click", (e) => {
      if (!toolOpen) return;
      if ((e.target as HTMLElement)?.closest?.("#arcade-nav-tools")) return;
      closeTool();
    });
    document.addEventListener("keydown", (e) => { if (e.key === "Escape") closeTool(); });
  }
}

function openTool(btn: HTMLElement, a: NavAction, index: number): void {
  const pop = document.createElement("div");
  pop.className = "nav-tool-pop";
  if (a.menu?.length) {
    pop.classList.add("nav-tool-menu");
    pop.innerHTML = a.menu.map((m, j) =>
      `<button class="nav-tool-item" type="button" data-arc-item="${j}"${m.disabled ? " disabled" : ""}>` +
      `${iconHtml(m.icon)}${esc(m.label ?? "")}</button>`).join("");
    pop.querySelectorAll<HTMLElement>("[data-arc-item]").forEach((row) => {
      row.addEventListener("click", (e) => {
        e.preventDefault();
        const m = a.menu?.[Number(row.dataset.arcItem)];
        closeTool();
        if (m && typeof m.onClick === "function") m.onClick();
      });
    });
  }
  btn.parentElement?.appendChild(pop);
  btn.setAttribute("aria-expanded", "true");
  // The panel callback runs after the element is in the document, so the game
  // can measure it and focus a field.
  const cleanup = typeof a.panel === "function" ? a.panel(pop) : undefined;
  toolOpen = { index, cleanup };
  pop.querySelector<HTMLElement>("input, button, select, textarea")?.focus();
}

const notifications = {
  /**
   * Mount a notifications bell (badge + dropdown) into `el`. Shows the player's
   * pending invites across ALL games. Accepting navigates to the invite's
   * `payload.url` by default, or calls `opts.onAccept(invite)` if provided (so a
   * game can handle its own invites in-app). Returns an unmount function.
   */
  mount(el: HTMLElement, opts: { onAccept?: (invite: Invite) => void } = {}): () => void {
    injectNotifStyles();
    el.classList.add("arc-notif");
    el.innerHTML =
      '<button class="arc-notif-btn" type="button" aria-haspopup="true" aria-expanded="false" title="Notifications">🔔' +
      '<span class="arc-notif-badge" hidden>0</span></button>' +
      '<div class="arc-notif-menu" hidden><div class="arc-notif-head">Notifications</div>' +
      '<div class="arc-notif-list"></div></div>';

    const btn = el.querySelector(".arc-notif-btn") as HTMLElement;
    const badge = el.querySelector(".arc-notif-badge") as HTMLElement;
    const menu = el.querySelector(".arc-notif-menu") as HTMLElement;
    const list = el.querySelector(".arc-notif-list") as HTMLElement;
    let current: Invite[] = [];

    function render(items: Invite[]) {
      current = items || [];
      const n = current.length;
      badge.textContent = String(n);
      badge.hidden = n === 0;
      list.innerHTML = n ? current.map(item).join("") : '<p class="arc-notif-empty">No notifications.</p>';
    }

    /**
     * The inbox carries more than invites now. An invite asks a question and so
     * gets Accept/Decline; an achievement is a statement and gets one dismiss,
     * because offering to "decline" an unlock you already earned is nonsense.
     */
    /**
     * What to call the game in a notice. The server sends a display name on
     * newer rows; older ones only carry the slug, so tidy that rather than
     * printing "my-game" at the player.
     */
    /** "just now", "5m", "2h", "3d", then a date. Short: it shares a line. */
    function when(iso: string): string {
      const t = Date.parse(iso);
      if (isNaN(t)) return "";
      const secs = Math.max(0, (Date.now() - t) / 1000);
      if (secs < 60) return "just now";
      if (secs < 3600) return `${Math.floor(secs / 60)}m`;
      if (secs < 86400) return `${Math.floor(secs / 3600)}h`;
      if (secs < 604800) return `${Math.floor(secs / 86400)}d`;
      return new Date(t).toLocaleDateString(undefined, { day: "numeric", month: "short" });
    }

    function gameLabel(inv: Invite): string {
      const named = (inv.payload as Record<string, unknown> | undefined)?.gameName;
      if (typeof named === "string" && named.trim()) return named.trim();
      return String(inv.game ?? "")
        .split("-")
        .filter(Boolean)
        .map((w) => w[0].toUpperCase() + w.slice(1))
        .join(" ");
    }

    function item(inv: Invite): string {
      const kicker =
        '<span class="arc-notif-kick">' +
        `<span class="arc-notif-game">${esc(gameLabel(inv))}</span>` +
        `<time class="arc-notif-when" datetime="${esc(String(inv.at ?? ""))}">${esc(when(String(inv.at ?? "")))}</time>` +
        "</span>";
      const acts = (yes: string, no: string) =>
        '<div class="arc-notif-actions">' +
        `<button class="arc-notif-act arc-notif-accept" data-act="accept" data-id="${esc(inv.id)}">${esc(yes)}</button>` +
        `<button class="arc-notif-act" data-act="decline" data-id="${esc(inv.id)}">${esc(no)}</button>`;

      if (inv.kind === "leaderboard") {
        const p = (inv.payload ?? {}) as Record<string, unknown>;
        return `<div class="arc-notif-item arc-notif-lb" data-id="${esc(inv.id)}">` +
          '<div class="arc-notif-text"><span class="arc-notif-icon">\u{1F3C5}</span><span class="arc-notif-body">' +
          kicker +
          `<strong>${esc(String(p.player ?? "Someone"))} passed your score</strong>` +
          `<em>${esc(String(p.score ?? ""))} to your ${esc(String(p.yourScore ?? ""))}</em>` +
          "</span></div>" +
          acts("View board", "Dismiss") +
          "</div></div>";
      }
      if (inv.kind === "achievement") {
        const p = (inv.payload ?? {}) as Record<string, unknown>;
        const icon = typeof p.icon === "string" && p.icon ? p.icon : "\u{1F3C6}";
        const art = icon.startsWith("/")
          ? `<img class="arc-notif-icon" src="${esc(icon)}" alt="">`
          : `<span class="arc-notif-icon">${esc(icon)}</span>`;
        const desc = String(p.description ?? "");
        return `<div class="arc-notif-item arc-notif-ach" data-id="${esc(inv.id)}">` +
          `<div class="arc-notif-text">${art}<span class="arc-notif-body">` +
          kicker +
          // The GS leads the title, the way it does on a cabinet row.
          `<span class="arc-notif-title"><em>${gsMark(Number(p.points ?? 0))}</em>` +
          `<strong>${esc(String(p.name ?? "Achievement unlocked"))}</strong></span>` +
          (desc ? `<span class="arc-notif-desc">${esc(desc)}</span>` : "") +
          "</span></div>" +
          acts("View", "Dismiss") +
          "</div></div>";
      }
      return `<div class="arc-notif-item" data-id="${esc(inv.id)}">` +
        '<div class="arc-notif-text"><span class="arc-notif-icon">\u2709\uFE0F</span><span class="arc-notif-body">' +
        kicker +
        `<strong>${esc(inv.from?.name ?? "Someone")} invited you</strong>` +
        `<em>${esc(inv.kind)}</em></span></div>` +
        acts("Accept", "Decline") +
        "</div></div>";
    }

    const setOpen = (open: boolean) => { menu.hidden = !open; btn.setAttribute("aria-expanded", open ? "true" : "false"); };
    const onBtn = (e: Event) => { e.stopPropagation(); setOpen(menu.hidden); };
    const onDoc = (e: Event) => { if (!menu.hidden && !el.contains(e.target as Node)) setOpen(false); };
    const onList = async (e: Event) => {
      const t = (e.target as HTMLElement)?.closest?.("[data-act]") as HTMLElement | null;
      if (!t) return;
      const id = t.dataset.id as string;
      const inv = current.find((i) => i.id === id);
      render(current.filter((i) => i.id !== id)); // optimistic
      try {
        if (t.dataset.act === "accept") {
          await invites.accept(id);
          const isNotice = inv && (inv.kind === "achievement" || inv.kind === "leaderboard");
          if (opts.onAccept && inv && !isNotice) opts.onAccept(inv);
          else if (inv && typeof inv.payload?.url === "string") location.assign(inv.payload.url as string);
        } else {
          await invites.decline(id);
        }
      } catch { /* poll reconciles */ }
    };

    btn.addEventListener("click", onBtn);
    document.addEventListener("click", onDoc);
    list.addEventListener("click", onList);
    const unsub = invites.onChange(render);

    return () => {
      unsub();
      btn.removeEventListener("click", onBtn);
      document.removeEventListener("click", onDoc);
      list.removeEventListener("click", onList);
      el.classList.remove("arc-notif");
      el.innerHTML = "";
    };
  },
};

// ---------------------------------------------------------------------------
// Achievements — per-game unlocks worth Gamesight Score (GS), declared in the
// game's achievements.json and validated server-side, so a game can only ever
// award its own. unlock() is safe to call repeatedly: the server ignores one
// the player already owns and reports `new: false`, which means games never
// have to track what they've already handed out.
//
// A new unlock raises the built-in popup (bottom-centre, Xbox-style) with a
// synthesized chime — no asset files, matching how the games do audio.
// Unlocks granted by a game's *backend* arrive through watch()'s poll instead.
// ---------------------------------------------------------------------------
const ACH_STYLE_ID = "arcade-achievement-styles";
const ACH_WRAP_ID = "arcade-achievement-toasts";
const ACH_MUTE_KEY = "arcade.achievements.muted";
const ACH_POLL_MS = 20000;
const ACH_TOAST_MS = 4600;
const ACH_OUT_MS = 320;

function injectAchievementStyles(): void {
  if (typeof document === "undefined" || document.getElementById(ACH_STYLE_ID)) return;
  const css = `
#${ACH_WRAP_ID}{position:fixed;left:50%;bottom:2.2rem;transform:translateX(-50%);z-index:2147483000;display:flex;flex-direction:column;align-items:center;gap:.5rem;pointer-events:none;font-family:"Space Grotesk",system-ui,sans-serif}
.arc-ach{position:relative;display:flex;align-items:center;gap:.85rem;min-width:19rem;max-width:min(92vw,27rem);padding:.7rem .95rem;border-radius:14px;background:linear-gradient(135deg,#1d1d42,#141430);border:1px solid #3a3a72;box-shadow:0 18px 44px #000c;color:#f5f7ff;overflow:hidden;opacity:0;transform:translateY(28px) scale(.94);transition:opacity .34s ease,transform .34s cubic-bezier(.2,1.3,.4,1)}
.arc-ach.in{opacity:1;transform:none}
.arc-ach.out{opacity:0;transform:translateY(10px) scale(.97);transition:opacity .28s ease,transform .28s ease}
.arc-ach::after{content:"";position:absolute;inset:0;pointer-events:none;background:linear-gradient(105deg,transparent 30%,#8a8aff38 47%,transparent 64%);transform:translateX(-120%)}
.arc-ach.in::after{animation:arc-ach-shine 1.1s .22s ease-out both}
@keyframes arc-ach-shine{to{transform:translateX(120%)}}
.arc-ach-icon{flex:0 0 auto;width:2.9rem;height:2.9rem;border-radius:10px;display:grid;place-items:center;font-size:1.5rem;line-height:1;background:#0b0f1c;border:1px solid #3a3a72;overflow:hidden}
.arc-ach-icon img{width:100%;height:100%;object-fit:cover;display:block}
.arc-ach-body{flex:1 1 auto;min-width:0}
.arc-ach-kicker{font-size:.63rem;font-weight:700;letter-spacing:.15em;text-transform:uppercase;color:#8a8aff;margin-bottom:.1rem}
.arc-ach-name{font-weight:700;font-size:.97rem;line-height:1.25;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
.arc-ach-desc{font-size:.78rem;color:#9a9ad6;line-height:1.3;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
.arc-ach-pts{flex:0 0 auto;align-self:stretch;display:flex;align-items:center;padding-left:.85rem;margin-left:.1rem;border-left:1px solid #2e2e5e;font-weight:800;font-size:1rem;color:#f5cf22;white-space:nowrap}
.arc-gs{display:inline-flex;align-items:center;gap:.3em;white-space:nowrap;font-variant-numeric:tabular-nums}
.arc-gs-badge{width:1em;height:1em;flex:0 0 auto;overflow:visible}
.arc-sr{position:absolute;width:1px;height:1px;margin:-1px;padding:0;overflow:hidden;clip-path:inset(50%);white-space:nowrap;border:0}
@media (prefers-reduced-motion:reduce){
.arc-ach{transform:none;transition:opacity .2s ease}
.arc-ach.out{transform:none;transition:opacity .2s ease}
.arc-ach.in::after{animation:none}
}`;
  const el = document.createElement("style");
  el.id = ACH_STYLE_ID;
  el.textContent = css;
  document.head.appendChild(el);
}

/**
 * The GS mark — a filled circle with "GS" knocked clean out, so whatever is
 * behind shows through the letters. The circle is `currentColor`, so it always
 * matches the text it sits beside, and it renders to the LEFT of the number
 * like a currency symbol.
 *
 * The knockout is an SVG mask rather than a drawn path so the letters are real
 * font glyphs. textLength condenses them to a fixed width, which both fits two
 * caps inside the circle and keeps the mark identical whether Poppins loaded or
 * a fallback did. Each instance gets its own mask id — identical markup would
 * still render, but duplicate ids in one document are invalid.
 */
/* Rounded hexagon with a geometric GS knocked out — drawn as paths, so the
   mark is identical everywhere and needs no webfont. */
const GS_HEX = "M 10.55 0.84 A 2.9 2.9 0 0 1 13.45 0.84 L 20.94 5.16 A 2.9 2.9 0 0 1 22.39 7.67 L 22.39 16.33 A 2.9 2.9 0 0 1 20.94 18.84 L 13.45 23.16 A 2.9 2.9 0 0 1 10.55 23.16 L 3.06 18.84 A 2.9 2.9 0 0 1 1.61 16.33 L 1.61 7.67 A 2.9 2.9 0 0 1 3.06 5.16 Z";
const GS_LETTERS = "M 10.95 9.55 L 10.95 8.78 A 0.78 0.78 0 0 0 10.17 8 L 7.75 8 A 1.2 1.2 0 0 0 6.55 9.2 L 6.55 14.8 A 1.2 1.2 0 0 0 7.75 16 L 9.75 16 A 1.2 1.2 0 0 0 10.95 14.8 L 10.95 13.59 A 0.57 0.57 0 0 0 10.38 13.02 L 9.81 13.02 M 17.45 8 L 15.25 8 A 1.2 1.2 0 0 0 14.05 9.2 L 14.05 10.8 A 1.2 1.2 0 0 0 15.25 12 L 16.25 12 A 1.2 1.2 0 0 1 17.45 13.2 L 17.45 14.8 A 1.2 1.2 0 0 1 16.25 16 L 14.05 16";

let gsSeq = 0;
function gsMark(points: number | string): string {
  const id = `arc-gs-k${++gsSeq}`;
  return (
    '<span class="arc-gs">' +
    '<svg class="arc-gs-badge" viewBox="0 0 24 24" aria-hidden="true">' +
    `<mask id="${id}"><path d="${GS_HEX}" fill="#fff"/>` +
    `<path d="${GS_LETTERS}" stroke="#000" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" fill="none"/></mask>` +
    `<path d="${GS_HEX}" fill="currentColor" mask="url(#${id})"/></svg>` +
    `<span>${esc(points)}</span>` +
    '<span class="arc-sr">Gamesight Score</span>' +
    "</span>"
  );
}

function achWrap(): HTMLElement {
  let wrap = document.getElementById(ACH_WRAP_ID);
  if (!wrap) {
    wrap = document.createElement("div");
    wrap.id = ACH_WRAP_ID;
    document.body.appendChild(wrap);
  }
  return wrap;
}

function achMuted(): boolean {
  try { return localStorage.getItem(ACH_MUTE_KEY) === "1"; } catch { return false; }
}

/**
 * The unlock sting: a rising major arpeggio finishing on a held octave.
 * Synthesized on the spot — nothing to download, nothing that can 404 mid-game.
 * The context is created on the first unlock, which in practice is well after
 * the gesture that started the game; if it's still suspended the popup just
 * plays silently rather than throwing.
 */
let achAudioCtx: AudioContext | null = null;
function achChime(): void {
  if (achMuted() || typeof window === "undefined") return;
  try {
    const Ctor = window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!Ctor) return;
    if (!achAudioCtx) achAudioCtx = new Ctor();
    if (achAudioCtx.state === "suspended") void achAudioCtx.resume();
    const ctx = achAudioCtx;
    const t0 = ctx.currentTime;
    const notes = [
      { f: 523.25, t: 0.00, d: 0.13, g: 0.09 }, // C5
      { f: 659.25, t: 0.08, d: 0.13, g: 0.09 }, // E5
      { f: 783.99, t: 0.16, d: 0.15, g: 0.10 }, // G5
      { f: 1046.5, t: 0.25, d: 0.62, g: 0.13 }, // C6, held
    ];
    for (const n of notes) {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = "triangle";
      osc.frequency.value = n.f;
      const start = t0 + n.t;
      gain.gain.setValueAtTime(0.0001, start);
      gain.gain.exponentialRampToValueAtTime(n.g, start + 0.014);
      gain.gain.exponentialRampToValueAtTime(0.0001, start + n.d);
      osc.connect(gain).connect(ctx.destination);
      osc.start(start);
      osc.stop(start + n.d + 0.03);
    }
  } catch { /* audio is decoration — never break a game over it */ }
}

// One popup at a time: simultaneous unlocks queue and play in order rather than
// stacking into an unreadable pile.
const achQueue: { a: Achievement; game: string }[] = [];
let achDraining = false;

function achShow(t: { a: Achievement; game: string }): Promise<void> {
  const a = t.a;
  return new Promise((resolve) => {
    injectAchievementStyles();
    const wrap = achWrap();
    const el = document.createElement("div");
    el.className = "arc-ach";
    el.setAttribute("role", "status");
    const icon = a.icon && a.icon.startsWith("/")
      ? `<img src="${esc(a.icon)}" alt="">`
      : esc(a.icon || "🏅");
    el.innerHTML =
      `<div class="arc-ach-icon">${icon}</div>` +
      '<div class="arc-ach-body">' +
      // The popup also fires on pages other than the game's own (watch() polls
      // for server-granted unlocks), so it says which game it came from.
      `<div class="arc-ach-kicker">Achievement unlocked${t.game ? " \u00b7 " + esc(t.game) : ""}</div>` +
      `<div class="arc-ach-name">${esc(a.name)}</div>` +
      `<div class="arc-ach-desc">${esc(a.description)}</div>` +
      "</div>" +
      `<div class="arc-ach-pts">${gsMark(a.points)}</div>`;
    wrap.appendChild(el);
    achChime();
    // Two frames so the browser paints the from-state before the transition.
    requestAnimationFrame(() => requestAnimationFrame(() => el.classList.add("in")));
    setTimeout(() => {
      el.classList.remove("in");
      el.classList.add("out");
      setTimeout(() => {
        el.remove();
        if (!wrap.childElementCount) wrap.remove();
        resolve();
      }, ACH_OUT_MS);
    }, ACH_TOAST_MS);
  });
}

async function achDrain(): Promise<void> {
  achDraining = true;
  while (achQueue.length) await achShow(achQueue.shift()!);
  achDraining = false;
}

/** Announce an unlock exactly once, whoever spotted it first (unlock() or the poll). */
const achSeen = new Set<string>();
const achListeners = new Set<(a: Achievement & { game: string }) => void>();
let achBaselined = false;
let achPoll: ReturnType<typeof setInterval> | null = null;

function achAnnounce(a: Achievement, game: string, toast = true, gameName?: string): void {
  const key = `${game}#${a.id}`;
  if (achSeen.has(key)) return;
  achSeen.add(key);
  // Listeners always fire. Suppressing the popup — per call or via
  // init({achievementToasts:false}) — must never cost a game its own callback.
  achListeners.forEach((cb) => cb({ ...a, game }));
  if (toast && config.achievementToasts && typeof document !== "undefined") {
    achQueue.push({ a, game: gameName || game });
    if (!achDraining) void achDrain();
  }
}

/**
 * Pull the player's unlocks and announce anything we haven't seen. The very
 * first pass only records a baseline — otherwise every achievement earned in a
 * past session would replay its popup the moment the game loads.
 */
async function achRefresh(): Promise<void> {
  let mine: MyAchievements | null;
  try { mine = await achievements.mine(); } catch { return; } // offline — try again next tick
  if (!mine) return; // signed out
  const oldestFirst = mine.recent.slice().reverse();
  if (!achBaselined) {
    achBaselined = true;
    for (const r of oldestFirst) achSeen.add(`${r.game}#${r.id}`);
    return;
  }
  for (const r of oldestFirst) achAnnounce(r, r.game, true, r.gameName);
}

const achievements = {
  /**
   * Award an achievement to the signed-in player. Already owned? The server
   * ignores it and reports `new: false` — so call it whenever the condition is
   * met, without tracking state yourself. Resolves null when signed out, and
   * rejects with ArcadeOfflineError when the API is unreachable.
   *
   * `{ toast: false }` skips the popup for this one unlock; onUnlock() listeners
   * still fire, so you can stage your own celebration.
   */
  async unlock(id: string, opts: { toast?: boolean } = {}): Promise<UnlockResult | null> {
    const game = requireGame();
    const res = await apiOrNull<UnlockResult>(
      `/games/${game}/achievements/${encodeURIComponent(id)}/unlock`,
      { method: "POST" }
    );
    if (res?.new) achAnnounce(res.achievement, game, opts.toast !== false, res.gameName);
    return res;
  },

  /** This game's full achievement list, with unlock counts and the caller's progress. */
  async list(): Promise<GameAchievements> {
    return api<GameAchievements>(`/games/${requireGame()}/achievements`);
  },

  /** Everyone who has earned one, in the order they earned it (first finder first). */
  async unlockers(id: string, limit = 50): Promise<Unlocker[]> {
    const capped = Math.min(Math.max(1, Math.floor(limit)), 200);
    const data = await api<{ unlockers: Unlocker[] }>(
      `/games/${requireGame()}/achievements/${encodeURIComponent(id)}/unlockers?limit=${capped}`
    );
    return data.unlockers;
  },

  /** The player's Gamesight Score across every game, or null when signed out. */
  async mine(): Promise<MyAchievements | null> {
    return apiOrNull<MyAchievements>("/achievements/me");
  },

  /**
   * Start watching for unlocks awarded by your game's *backend* (verified
   * server-side grants), so those raise a popup too. Client unlocks via
   * unlock() pop immediately and don't need this. Idempotent.
   */
  watch(): void {
    if (achPoll || typeof window === "undefined") return;
    void achRefresh();
    achPoll = setInterval(() => void achRefresh(), ACH_POLL_MS);
    window.addEventListener("focus", () => void achRefresh());
  },

  /** Run a callback on every new unlock. Starts watch(). Returns an unsubscribe fn. */
  onUnlock(callback: (a: Achievement & { game: string }) => void): () => void {
    achListeners.add(callback);
    achievements.watch();
    return () => achListeners.delete(callback);
  },

  /**
   * Show the unlock popup for an arbitrary definition, without touching the
   * server. Awards nothing and doesn't mark anything as seen — for building and
   * tuning a celebration, and for the arcade's /debug page.
   */
  preview(a: Achievement, gameName = ""): void {
    if (typeof document === "undefined") return;
    achQueue.push({ a, game: gameName });
    if (!achDraining) void achDrain();
  },

  /** Silence the unlock chime (the popup still shows). Persists on this device. */
  setMuted(muted: boolean): void {
    try { localStorage.setItem(ACH_MUTE_KEY, muted ? "1" : "0"); } catch { /* private mode */ }
  },

  /** Whether the unlock chime is silenced on this device. */
  muted(): boolean {
    return achMuted();
  },
};

export default { init, leaderboard, achievements, auth, realtime, voice, invites, players, notifications, nav, ArcadeOfflineError };
export type { Achievement, ArcadeConfig, NavAction, NavBack, NavDrawer, NavStat, ArcadeUser, ArcadeVoice, BoardOptions, GameAchievement, GameAchievements, Invite, MyAchievements, MyScore, RealtimeRoom, RoomMember, ScoreEntry, SubmitOptions, Unlocker, UnlockResult, VoiceSession, VoiceState };
